import { GameState, Position, Player, Difficulty } from '../types/game';
import { GAME_CONFIG } from './gameLogic';
import { useSkill } from './skillSystem';

// 简单的AI决策系统
const getAIMove = (gameState: GameState, difficulty: Difficulty): Position | null => {
  const aiPlayer = 'white';
  const humanPlayer = 'black';

  // 首先检查AI是否能立即获胜
  for (let row = 0; row < GAME_CONFIG.boardSize; row++) {
    for (let col = 0; col < GAME_CONFIG.boardSize; col++) {
      if (gameState.board[row][col] === null) {
        if (canWin(gameState.board, { row, col }, aiPlayer)) {
          return { row, col };
        }
      }
    }
  }

  // 然后检查是否需要阻止玩家获胜
  for (let row = 0; row < GAME_CONFIG.boardSize; row++) {
    for (let col = 0; col < GAME_CONFIG.boardSize; col++) {
      if (gameState.board[row][col] === null) {
        if (canWin(gameState.board, { row, col }, humanPlayer)) {
          return { row, col };
        }
      }
    }
  }

  // 根据难度选择策略
  if (difficulty === 'easy') {
    return getEasyMove(gameState);
  } else if (difficulty === 'medium') {
    return getMediumMove(gameState);
  } else {
    return getHardMove(gameState);
  }
};

// 检查在指定位置下棋是否能获胜
const canWin = (board: GameState['board'], position: Position, player: Player): boolean => {
  const directions = [[0, 1], [1, 0], [1, 1], [1, -1]];

  for (const [dx, dy] of directions) {
    let count = 1;

    // 正方向检查
    for (let i = 1; i < 5; i++) {
      const newRow = position.row + dx * i;
      const newCol = position.col + dy * i;
      if (newRow >= 0 && newRow < GAME_CONFIG.boardSize &&
          newCol >= 0 && newCol < GAME_CONFIG.boardSize &&
          board[newRow][newCol] === player) {
        count++;
      } else {
        break;
      }
    }

    // 反方向检查
    for (let i = 1; i < 5; i++) {
      const newRow = position.row - dx * i;
      const newCol = position.col - dy * i;
      if (newRow >= 0 && newRow < GAME_CONFIG.boardSize &&
          newCol >= 0 && newCol < GAME_CONFIG.boardSize &&
          board[newRow][newCol] === player) {
        count++;
      } else {
        break;
      }
    }

    if (count >= 5) {
      return true;
    }
  }

  return false;
};

// 简单难度：随机但有基础防守
const getEasyMove = (gameState: GameState): Position | null => {
  // 首先检查是否需要防守四连（简单AI也要会防守）
  for (let row = 0; row < GAME_CONFIG.boardSize; row++) {
    for (let col = 0; col < GAME_CONFIG.boardSize; col++) {
      if (gameState.board[row][col] === null) {
        if (shouldBlockFour(gameState.board, { row, col })) {
          return { row, col };
        }
      }
    }
  }

  // 然后检查是否需要防守三连（重要）
  for (let row = 0; row < GAME_CONFIG.boardSize; row++) {
    for (let col = 0; col < GAME_CONFIG.boardSize; col++) {
      if (gameState.board[row][col] === null) {
        if (shouldBlockThree(gameState.board, { row, col })) {
          return { row, col };
        }
      }
    }
  }

  // 否则随机下棋
  const availablePositions: Position[] = [];

  for (let row = 0; row < GAME_CONFIG.boardSize; row++) {
    for (let col = 0; col < GAME_CONFIG.boardSize; col++) {
      if (gameState.board[row][col] === null) {
        availablePositions.push({ row, col });
      }
    }
  }

  if (availablePositions.length === 0) return null;

  // 偏向中心位置
  const centerBias = availablePositions.map(pos => {
    const distanceFromCenter = Math.abs(pos.row - 7.5) + Math.abs(pos.col - 7.5);
    const score = Math.random() + (15 - distanceFromCenter) * 0.1;
    return { pos, score };
  });

  centerBias.sort((a, b) => b.score - a.score);
  return centerBias[0].pos;
};

// 中等难度：积极防守
const getMediumMove = (gameState: GameState): Position | null => {
  // 首先检查是否需要防守四连（最紧急）
  for (let row = 0; row < GAME_CONFIG.boardSize; row++) {
    for (let col = 0; col < GAME_CONFIG.boardSize; col++) {
      if (gameState.board[row][col] === null) {
        if (shouldBlockFour(gameState.board, { row, col })) {
          return { row, col };
        }
      }
    }
  }

  // 立即检查是否需要防守三连（关键！）
  for (let row = 0; row < GAME_CONFIG.boardSize; row++) {
    for (let col = 0; col < GAME_CONFIG.boardSize; col++) {
      if (gameState.board[row][col] === null) {
        if (shouldBlockThree(gameState.board, { row, col })) {
          return { row, col };
        }
      }
    }
  }

  // 检查是否需要防守二连（预判防守）
  for (let row = 0; row < GAME_CONFIG.boardSize; row++) {
    for (let col = 0; col < GAME_CONFIG.boardSize; col++) {
      if (gameState.board[row][col] === null) {
        if (shouldBlockTwo(gameState.board, { row, col })) {
          return { row, col };
        }
      }
    }
  }

  // 否则随机下棋
  return getEasyMove(gameState);
};

// 困难难度：更智能的防守和进攻
const getHardMove = (gameState: GameState): Position | null => {
  const scoredPositions: Array<{pos: Position, score: number}> = [];

  for (let row = 0; row < GAME_CONFIG.boardSize; row++) {
    for (let col = 0; col < GAME_CONFIG.boardSize; col++) {
      if (gameState.board[row][col] === null) {
        const score = evaluatePositionSimple(gameState.board, { row, col });
        scoredPositions.push({ pos: { row, col }, score });
      }
    }
  }

  if (scoredPositions.length === 0) return null;

  scoredPositions.sort((a, b) => b.score - a.score);
  return scoredPositions[0].pos;
};

// 简单的位置评估
const evaluatePositionSimple = (board: GameState['board'], position: Position): number => {
  let score = 0;
  const aiPlayer = 'white';
  const humanPlayer = 'black';

  // 检查四个方向
  const directions = [[0, 1], [1, 0], [1, 1], [1, -1]];

  for (const [dx, dy] of directions) {
    // AI的连子数
    let aiCount = 1;
    // 人类的连子数
    let humanCount = 1;

    // 正方向检查
    for (let i = 1; i <= 4; i++) {
      const newRow = position.row + dx * i;
      const newCol = position.col + dy * i;

      if (newRow >= 0 && newRow < GAME_CONFIG.boardSize &&
          newCol >= 0 && newCol < GAME_CONFIG.boardSize) {
        if (board[newRow][newCol] === aiPlayer) {
          aiCount++;
        } else if (board[newRow][newCol] === humanPlayer) {
          humanCount++;
        } else {
          break;
        }
      }
    }

    // 反方向检查
    for (let i = 1; i <= 4; i++) {
      const newRow = position.row - dx * i;
      const newCol = position.col - dy * i;

      if (newRow >= 0 && newRow < GAME_CONFIG.boardSize &&
          newCol >= 0 && newCol < GAME_CONFIG.boardSize) {
        if (board[newRow][newCol] === aiPlayer) {
          aiCount++;
        } else if (board[newRow][newCol] === humanPlayer) {
          humanCount++;
        } else {
          break;
        }
      }
    }

    // 评分
    if (humanCount >= 4) score += 10000; // 必须防守四连
    if (humanCount >= 3) score += 1000;  // 防守三连
    if (humanCount >= 2) score += 100;   // 防守二连

    if (aiCount >= 4) score += 8000;  // 四连进攻
    if (aiCount >= 3) score += 800;   // 三连进攻
    if (aiCount >= 2) score += 80;    // 二连进攻
  }

  // 中心位置加分
  const centerDistance = Math.abs(position.row - 7.5) + Math.abs(position.col - 7.5);
  score += (15 - centerDistance) * 5;

  return score;
};

// 检查是否需要防守三连
const shouldBlockThree = (board: GameState['board'], position: Position): boolean => {
  const directions = [[0, 1], [1, 0], [1, 1], [1, -1]];

  for (const [dx, dy] of directions) {
    let count = 1;
    let openEnds = 0;

    // 正方向检查
    for (let i = 1; i <= 4; i++) {
      const newRow = position.row + dx * i;
      const newCol = position.col + dy * i;
      if (newRow >= 0 && newRow < GAME_CONFIG.boardSize &&
          newCol >= 0 && newCol < GAME_CONFIG.boardSize) {
        if (board[newRow][newCol] === 'black') {
          count++;
        } else if (board[newRow][newCol] === null) {
          openEnds++;
          break;
        } else {
          break;
        }
      }
    }

    // 反方向检查
    for (let i = 1; i <= 4; i++) {
      const newRow = position.row - dx * i;
      const newCol = position.col - dy * i;
      if (newRow >= 0 && newRow < GAME_CONFIG.boardSize &&
          newCol >= 0 && newCol < GAME_CONFIG.boardSize) {
        if (board[newRow][newCol] === 'black') {
          count++;
        } else if (board[newRow][newCol] === null) {
          openEnds++;
          break;
        } else {
          break;
        }
      }
    }

    // 活三：三子相连且两端开放
    if (count === 3 && openEnds >= 2) {
      return true;
    }
    // 冲三：三子相连且一端开放
    if (count === 3 && openEnds >= 1) {
      return true;
    }
  }

  return false;
};

// 检查是否需要防守二连
const shouldBlockTwo = (board: GameState['board'], position: Position): boolean => {
  const directions = [[0, 1], [1, 0], [1, 1], [1, -1]];

  for (const [dx, dy] of directions) {
    let count = 1;
    let openEnds = 0;

    // 正方向检查
    for (let i = 1; i <= 4; i++) {
      const newRow = position.row + dx * i;
      const newCol = position.col + dy * i;
      if (newRow >= 0 && newRow < GAME_CONFIG.boardSize &&
          newCol >= 0 && newCol < GAME_CONFIG.boardSize) {
        if (board[newRow][newCol] === 'black') {
          count++;
        } else if (board[newRow][newCol] === null) {
          openEnds++;
          break;
        } else {
          break;
        }
      }
    }

    // 反方向检查
    for (let i = 1; i <= 4; i++) {
      const newRow = position.row - dx * i;
      const newCol = position.col - dy * i;
      if (newRow >= 0 && newRow < GAME_CONFIG.boardSize &&
          newCol >= 0 && newCol < GAME_CONFIG.boardSize) {
        if (board[newRow][newCol] === 'black') {
          count++;
        } else if (board[newRow][newCol] === null) {
          openEnds++;
          break;
        } else {
          break;
        }
      }
    }

    // 活二：两子相连且两端开放
    if (count === 2 && openEnds >= 2) {
      return true;
    }
  }

  return false;
};

// 检查是否需要防守四连
const shouldBlockFour = (board: GameState['board'], position: Position): boolean => {
  const directions = [[0, 1], [1, 0], [1, 1], [1, -1]];

  for (const [dx, dy] of directions) {
    let count = 1;
    let openEnds = 0;

    // 正方向检查
    for (let i = 1; i <= 4; i++) {
      const newRow = position.row + dx * i;
      const newCol = position.col + dy * i;
      if (newRow >= 0 && newRow < GAME_CONFIG.boardSize &&
          newCol >= 0 && newCol < GAME_CONFIG.boardSize) {
        if (board[newRow][newCol] === 'black') {
          count++;
        } else if (board[newRow][newCol] === null) {
          openEnds++;
          break;
        } else {
          break;
        }
      }
    }

    // 反方向检查
    for (let i = 1; i <= 4; i++) {
      const newRow = position.row - dx * i;
      const newCol = position.col - dy * i;
      if (newRow >= 0 && newRow < GAME_CONFIG.boardSize &&
          newCol >= 0 && newCol < GAME_CONFIG.boardSize) {
        if (board[newRow][newCol] === 'black') {
          count++;
        } else if (board[newRow][newCol] === null) {
          openEnds++;
          break;
        } else {
          break;
        }
      }
    }

    // 活四：四子相连且两端开放（必胜）
    if (count === 4 && openEnds >= 2) {
      return true;
    }
    // 冲四：四子相连且一端开放（必须立即拦截）
    if (count === 4 && openEnds >= 1) {
      return true;
    }
  }

  return false;
};

// AI技能使用逻辑
const getAISkillUse = (gameState: GameState, difficultyLevel: number = 1): { skill: string, needsTarget: boolean } | null => {
  const aiPlayer = 'white';
  const aiSkills = gameState.skills[aiPlayer];
  const availableSkills = aiSkills.filter(skill =>
    skill.cooldownRemaining === 0 &&
    gameState.energy[aiPlayer] >= skill.energyCost
  );

  if (availableSkills.length === 0) return null;

  // 根据难度等级调整技能使用策略
  const skillUseChance = Math.min(0.2 + (difficultyLevel * 0.15), 0.8);

  if (Math.random() > skillUseChance) {
    return null;
  }

  // 选择一个技能
  const selectedSkill = availableSkills[Math.floor(Math.random() * availableSkills.length)];

  // 检查技能是否需要目标
  const needsTarget = selectedSkill.description.includes('指定位置') ||
                     selectedSkill.description.includes('目标位置') ||
                     selectedSkill.description.includes('位置');

  return { skill: selectedSkill.id, needsTarget };
};

// 模拟AI思考时间
export const simulateAIThinking = async (difficulty: Difficulty): Promise<void> => {
  let delay: number;

  switch (difficulty) {
    case 'easy':
      delay = 500 + Math.random() * 1000; // 0.5-1.5秒
      break;
    case 'medium':
      delay = 1000 + Math.random() * 1500; // 1-2.5秒
      break;
    case 'hard':
      delay = 1500 + Math.random() * 2000; // 1.5-3.5秒
      break;
    default:
      delay = 1000;
  }

  return new Promise(resolve => setTimeout(resolve, delay));
};

// 主要AI决策函数
export const getAIDecision = (gameState: GameState, difficultyLevel: number = 1): { move?: Position; skill?: string } => {
  // 根据难度等级映射到难度设置
  const difficultyMap: { [key: number]: Difficulty } = {
    1: 'easy',
    2: 'easy',
    3: 'medium',
    4: 'medium',
    5: 'hard'
  };

  const difficulty = difficultyMap[difficultyLevel] || 'medium';

  // AI技能使用逻辑
  const skillUse = getAISkillUse(gameState, difficultyLevel);
  if (skillUse) {
    if (skillUse.needsTarget) {
      // 需要目标位置的技能，找到最佳位置
      const movePosition = getAIMove(gameState, difficulty);
      if (movePosition) {
        return { skill: skillUse.skill, move: movePosition };
      }
    } else {
      // 不需要目标位置的技能
      return { skill: skillUse.skill };
    }
  }

  // 如果不使用技能，选择移动位置
  const movePosition = getAIMove(gameState, difficulty);
  return movePosition ? { move: movePosition } : {};
};